import { CommonModule } from '@angular/common'
import { Component, inject, Input } from '@angular/core'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatTooltip } from '@angular/material/tooltip'
import { RouterLink } from '@angular/router'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { Call, CallsService, MetricType } from '../calls.service'
import { MatDialog } from '@angular/material/dialog'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { formatCounterpartyNames } from '../utils'
import { InfoDialogComponent } from '../info-dialog/info-dialog.component'
import { differenceInHours } from 'date-fns'
import { firstValueFrom } from 'rxjs'
import { ToastService } from '../toast.service'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MetricsService } from '../metrics.service'

@Component({
  selector: 'app-upcoming-meeting-card',
  standalone: true,
  imports: [CommonModule, RouterLink, MatIcon, MatIconButton, TranslateModule, MatTooltip, MatSlideToggle, MatMenu, MatMenuItem, MatMenuTrigger],
  templateUrl: './upcoming-meeting-card.component.html',
  styleUrl: './upcoming-meeting-card.component.scss',
})
export class UpcomingMeetingCardComponent {
  @Input() call!: Call

  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  translateService = inject(TranslateService)
  toastService = inject(ToastService)
  metricsService = inject(MetricsService)
  excludeCallMutation = this.callsService.toggleExcludeCallMutation()
  joinNowMutation = this.callsService.joinNowMutation()
  forceDisableJoiningMutation = this.callsService.forceDisableJoiningMutation()
  deleteCallMutation = this.callsService.deleteCallMutation()

  get isMultipleAgendaType(): boolean {
    return this.call.agendaType === 'MULTIPLE'
  }

  get hasLostCalendarSync(): boolean {
    return this.call.calendarSyncLostAt !== null
  }

  forceDisableJoining(call: Call): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.FORCE_DISABLE_DIALOG.TITLE'),
        description: this.translateService.instant('CALLS.FORCE_DISABLE_DIALOG.DESCRIPTION', { callName: call.name }),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.forceDisableJoiningMutation.mutate(call.id)
      }
    })
  }

  get displayDeleteButton(): boolean {
    return this.call.type === 'LIVE'
  }

  delete(call: Call): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.DELETE.DIALOG_TITLE'),
        description: this.translateService.instant('CALLS.DELETE.DIALOG_BODY', { callName: call.name }),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.deleteCallMutation.mutateAsync(call.id)
      }
    })
  }

  onMenuClick(event: Event): boolean {
    event.stopPropagation()
    return false
  }

  async toggleExclude(callId: number, checked: boolean): Promise<void> {
    await this.excludeCallMutation.mutateAsync({ callId, isSetToJoin: checked })
    if (!checked && this.call.botInstructedToJoinByOtherUsers) {
      this.dialog.open(InfoDialogComponent, {
        width: '400px',
        data: {
          title: this.translateService.instant('CALLS.UPCOMING_MEETING.INCLUDED_BY_ANOTHER_USER_DIALOG'),
        },
      })
    }
  }

  get formattedCounterpartyNames(): string {
    return formatCounterpartyNames(this.call.counterpartyNames)
  }

  get botWillJoinAgainstUserSelection(): boolean {
    return !this.call.isSetToJoin && this.call.botInstructedToJoinByOtherUsers
  }

  get excludeToggleTooltip(): string {
    if (this.botWillJoinAgainstUserSelection) {
      return this.translateService.instant('CALLS.UPCOMING_MEETING.INCLUDED_BY_ANOTHER_USER')
    }
    if (this.call.isSetToJoin) {
      return this.translateService.instant('CALLS.UPCOMING_MEETING.EXCLUDE')
    }
    return this.translateService.instant('CALLS.UPCOMING_MEETING.INCLUDE')
  }

  onCardClick(): void {
    this.metricsService.sendMetric(this.call.id, MetricType.CLICK_ON_UPCOMING_MEETING)
  }

  get allowJoiningNow(): boolean {
    return differenceInHours(this.call.date, new Date()) < 2
  }

  async joinNow() {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.JOIN_NOW.DIALOG_TITLE'),
        description: this.translateService.instant('CALLS.JOIN_NOW.DIALOG_DESCRIPTION'),
      },
    })

    const result = await firstValueFrom(dialogRef.afterClosed())
    if (result) {
      await this.joinNowMutation.mutateAsync(this.call.id)
      this.toastService.show('CALLS.JOIN_NOW.SUCCESS_CONFIRMATION')
    }
  }
}
